"use client";

import {
  Form,
  FormCheckbox,
  FormInput,
  FormSelect,
  FormStatus,
  FormTextarea,
  useFormStatus,
} from "@/components/primitives/Form";
import { Button } from "@/components/primitives/Button";
import { ConsentPrivacyLabel } from "@/components/forms/ConsentPrivacyLabel";
import { getFormsCopy } from "@/content/forms";
import { submitPartner } from "@/lib/api/endpoints";
import { buildFormConsentRecord } from "@/lib/consent";
import type { Locale } from "@/lib/i18n/config";
import { useLocale } from "@/lib/i18n/provider";
import { partnerInputSchema } from "@/lib/api/types";
import { withAttribution } from "@/lib/utm";

export function PartnerForm({ className = "" }: { className?: string }) {
  const { locale } = useLocale();
  const forms = getFormsCopy(locale);

  return (
    <Form
      schema={partnerInputSchema}
      trackingName="partner"
      successMessage={forms.partner.successMessage}
      className={className}
      onSubmit={async (values, signal) => {
        const { consentGiven, ...input } = values;
        if (!consentGiven) return;

        const consentRecord = buildFormConsentRecord({
          consentText: forms.partner.consentTextRecord,
          locale,
          formSource: "partner",
        });

        const res = await submitPartner(
          withAttribution(
            { ...input, locale },
            { entryPoint: "partner_apply", sourceBase: "partner" },
          ),
          consentRecord,
          signal,
        );
        if (res.ticketId) {
          return `${forms.partner.ticketSuccessPrefix} ${res.ticketId} ${forms.partner.ticketCreatedSuffix} ${forms.partner.ticketFollowUp}`;
        }
      }}
    >
      <div className="flex flex-col gap-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <FormInput
            name="name"
            label={forms.partner.nameLabel}
            required
            autoComplete="name"
          />
          <FormInput
            name="email"
            type="email"
            label={forms.partner.emailLabel}
            required
            autoComplete="email"
          />
        </div>
        <FormInput
          name="company"
          label={forms.partner.companyLabel}
          required
          autoComplete="organization"
        />
        <FormSelect
          name="partnerType"
          label={forms.partner.partnerTypeLabel}
          required
          options={forms.partner.types}
        />
        <FormTextarea
          name="message"
          label={forms.partner.messageLabel}
          required
          rows={4}
          placeholder={forms.partner.messagePlaceholder}
        />
        <FormCheckbox
          name="consentGiven"
          required
          label={<ConsentPrivacyLabel text={forms.partner.consentLabel} />}
        />
        <Submit locale={locale} />
        <FormStatus />
      </div>
    </Form>
  );
}

function Submit({ locale }: { locale: Locale }) {
  const forms = getFormsCopy(locale);
  const status = useFormStatus();
  const busy = status === "submitting";
  return (
    <Button type="submit" variant="primary" size="lg" disabled={busy}>
      {busy ? forms.partner.busyLabel : forms.partner.submitLabel}
    </Button>
  );
}
